import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CTA from '../components/CTA';
import OurCommitment from '../components/OurCommitment';
import { generateCTAMessage } from '../utils/ctaMessages.js';

const CaseStudiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIndustries, setSelectedIndustries] = useState(() => {
    const q = searchParams.get('industries');
    return q ? q.split(',').filter(Boolean) : [];
  });
  const [isFiltering, setIsFiltering] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    if (selectedIndustries.length === 0) {
      searchParams.delete('industries');
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ industries: selectedIndustries.join(',') }, { replace: true });
    }
  }, [selectedIndustries, searchParams, setSearchParams]);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleIndustry = (id) => {
    setIsFiltering(true);
    
    setSelectedIndustries((prev) => {
      if (id === 'all') return [];
      const next = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
      return next;
    });
    
    // Smooth scroll to case studies section after a short delay
    setTimeout(() => {
      const caseStudiesSection = document.getElementById('case-studies-grid');
      if (caseStudiesSection) {
        caseStudiesSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
      setIsFiltering(false);
    }, 300);
  };

  const industryGradient = {
    healthcare: 'badge-gradient-blue',
    transport: 'badge-gradient-teal',
    education: 'badge-gradient-green',
    cybersecurity: 'badge-gradient-red',
    retail: 'badge-gradient-warm',
    finance: 'badge-gradient-blue',
    manufacturing: 'badge-gradient-neutral'
  };
  
  const caseStudies = useMemo(() => [
    {
      id: 'healthcare-appointment-assistant',
      title: 'MediConnect Pro - Smart Healthcare Assistant',
      industry: 'healthcare',
      client: 'MediConnect AI',
      challenge: 'Healthcare providers struggled with complex appointment scheduling processes, long patient wait times, and difficulty in providing multilingual support. Patients needed help understanding lab reports and finding appropriate specialists. Manual appointment management was time-consuming and error-prone.',
      solution: 'Engineered a comprehensive AI-powered healthcare assistant using OpenAI models with FastMCP client/server architecture. The system handles multilingual appointment booking, lab report analysis with medical insights, specialist recommendations, automated email notifications, and voice chat capabilities for seamless patient interaction.',
      results: [
        '85% of appointment bookings automated across multiple languages',
        '70% reduction in scheduling time through intelligent routing',
        '90% patient satisfaction with voice and text interaction',
        '60% decrease in administrative workload for healthcare staff'
      ],
      technologies: ['OpenAI GPT', 'FastMCP', 'Python', 'Voice Chat API', 'Email Automation', 'Multi-language Support', 'Lab Report Analysis', 'Medical NLP', 'Appointment Scheduling API', 'Docker', 'AWS'],
      duration: '4 months',
      teamSize: '3 AI Engineers + 1 Healthcare Integration Specialist + 1 DevOps Engineer',
      image: '/images/healthcare-appointment-assistant.webp',
      metrics: {
        before: 'Manual appointment booking with language barriers',
        after: 'AI-powered multilingual appointment assistant with voice chat',
        improvement: '85% automation, 70% faster scheduling'
      }
    },
    {
      id: 'healthcare-transport-automation',
      title: 'AxleFlow - Intelligent Transport Dispatch System',
      industry: 'healthcare',
      client: 'Axle Health',
      challenge: 'Axle Health faced critical operational bottlenecks with patient transport coordination. Dispatchers were overwhelmed by 500+ daily transport requests, leading to 45-minute average response times, missed appointments, and declining patient satisfaction scores. Manual processes created inconsistent service quality and compliance risks.',
      solution: 'Engineered an enterprise-grade GenAI transport assistant using advanced RAG architecture with LangChain. The system integrates with Epic EHR, hospital scheduling systems, and ambulance dispatch APIs. Features include real-time status tracking, automated scheduling optimization, compliance logging, and seamless handoff protocols for complex cases requiring human intervention.',
      results: [
        '80% of routine transport queries automated (400+ daily requests)',
        '60% reduction in average response time (45min → 18min)',
        '95% patient satisfaction score improvement',
        '40% reduction in missed transport appointments'
      ],
      technologies: ['GenAI', 'RAG', 'LangChain', 'FastAPI', 'Python', 'Epic EHR Integration', 'Redis', 'PostgreSQL', 'Docker', 'AWS ECS', 'Healthcare APIs'],
      duration: '3 months',
      teamSize: '2 AI Engineers + 1 DevOps Engineer + 1 Healthcare Integration Specialist',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual dispatch with 45min response times',
        after: 'AI-powered automation with 18min response',
        improvement: '80% automation, 60% faster response'
      }
    },
    {
      id: 'auto-lease-chatbot',
      title: 'SeezAuto - AI Sales Assistant for Car Leasing',
      industry: 'transport',
      client: 'Seez',
      challenge: 'High inbound inquiry volume and inconsistent, manual responses were causing lead drop‑off across web and mobile channels.',
      solution: 'Implemented a production‑grade sales copilot using RAG with LangChain, deployed on FastAPI and Docker on AWS EC2. The copilot handles pricing, eligibility, inventory, and test‑drive bookings, and logs all conversations into the CRM.',
      results: [
        '15% increase in car sales post‑deployment',
        '60% faster first‑response times',
        '35% reduction in call‑center load'
      ],
      technologies: ['LLMs', 'RAG', 'LangChain', 'FastAPI', 'Docker', 'AWS EC2'],
      duration: '3 months',
      teamSize: '3 AI Engineers + 1 DevOps Engineer',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual sales chats, slow responses',
        after: '24/7 AI chatbot with instant answers',
        improvement: '15% sales increase'
      }
    },
    {
      id: 'education-rag-teacher-assistant',
      title: 'SullyEdu - Smart Teacher Assistant Platform',
      industry: 'education',
      client: 'Sully.ai',
      challenge: 'Teachers spent several hours daily reconciling submissions, chasing non‑submitters, and maintaining spreadsheets.',
      solution: 'Built an agentic RAG assistant with LangChain and LangGraph that queries submission systems and automatically emails students who have not submitted, with an auditable trail.',
      results: [
        '5 hours/day saved per teacher',
        'Automated follow‑ups with compliance trail',
        'Higher on‑time submission rates'
      ],
      technologies: ['LangChain', 'LangGraph', 'Agentic AI', 'Email Automation'],
      duration: '2 months',
      teamSize: '2 AI Engineers + 1 Integration Engineer',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual follow‑ups and spreadsheet tracking',
        after: 'Automated querying and reminder emails',
        improvement: '5 hrs/day saved'
      }
    },
    {
      id: 'cyber-email-classifier',
      title: 'SecureMail Pro - Advanced Email Security Suite',
      industry: 'cybersecurity',
      client: 'Upwork Client Project',
      challenge: 'SOC analysts were overwhelmed by phishing and spam, with high false‑negative risk and slow triage.',
      solution: 'Delivered an NLP classifier using TF‑IDF/Word2Vec features with Random Forest and anomaly detection to prioritize and block malicious emails before inbox delivery.',
      results: [
        '95% classification accuracy',
        '40% fewer malicious emails reaching inboxes',
        'Reduced manual triage time'
      ],
      technologies: ['TF-IDF', 'Word2Vec', 'Random Forest', 'Anomaly Detection', 'Python'],
      duration: '3 months',
      teamSize: '2 ML Engineers + 1 Security Analyst',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'High malicious email volume and false negatives',
        after: 'Automated screening with high precision',
        improvement: '40% fewer malicious emails'
      }
    },
    {
      id: 'retail-recommendation-engine',
      title: 'ShopSmart AI - Personalized Shopping Engine',
      industry: 'retail',
      client: 'Upwork Client Project',
      challenge: 'Generic recommendations and slow data access limited conversion and average order value.',
      solution: 'Launched a recommendation platform using Collaborative Filtering and feature‑engineered ML (Random Forest, GBM), with Redshift ETL and storage optimization for low‑latency serving.',
      results: [
        '$1M revenue influenced',
        'Improved conversion and AOV',
        '40% faster data access (compression/indexing)'
      ],
      technologies: ['Collaborative Filtering', 'Pandas', 'NumPy', 'AWS Redshift', 'Random Forest', 'GBM'],
      duration: '4 months',
      teamSize: '3 Data Scientists + 1 Data Engineer',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Generic recommendations and slow data access',
        after: 'Personalized engine with optimized storage',
        improvement: '$1M revenue impact'
      }
    },
    {
      id: 'banking-kyc-ocr',
      title: 'MandolinKYC - Smart Document Verification System',
      industry: 'finance',
      client: 'Mandolin',
      challenge: 'Manual document checks slowed onboarding and created data‑entry errors.',
      solution: 'Developed an image‑preprocessed OCR pipeline using easyocr to extract name, DOB, address, and ID numbers with validation hooks and review workflows.',
      results: [
        '40% faster KYC verification',
        'Higher data accuracy and fewer rejections',
        'Reduced manual workload'
      ],
      technologies: ['easyocr', 'Python', 'OCR', 'Image Preprocessing'],
      duration: '6 weeks',
      teamSize: '2 ML Engineers',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual KYC checks taking days',
        after: 'Automated extraction with validation',
        improvement: '40% faster verification'
      }
    },
    {
      id: 'telecom-sms-bert',
      title: 'SMSGuard - Mobile Security Protection Suite',
      industry: 'cybersecurity',
      client: 'Upwork Client Project',
      challenge: 'Subscribers were targeted by malicious SMS, driving security incidents and complaints.',
      solution: 'Trained and deployed BERT‑based classifiers with robust NLP preprocessing and continuous monitoring to stop SMS‑borne threats in production.',
      results: [
        '98% classification accuracy',
        '40% decrease in security incidents',
        'Improved customer trust and safety'
      ],
      technologies: ['BERT', 'Python', 'NLP', 'MLOps'],
      duration: '3 months',
      teamSize: '2 ML Engineers + 1 MLOps Engineer',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Frequent SMS‑based attacks',
        after: 'High‑accuracy classifier in production',
        improvement: '40% fewer incidents'
      }
    },
    {
      id: 'genai-interview-assistant',
      title: 'InterviewPro - AI-Powered Career Coach',
      industry: 'education',
      client: 'Upwork Client Project',
      challenge: 'Candidates needed real‑time coaching, note‑taking, and feedback to improve interview outcomes.',
      solution: 'Built a desktop assistant with GPT‑4o, Whisper, and RAG for live guidance, answer drafting, and knowledge retrieval, packaged for frictionless installs.',
      results: [
        '70% increase in user engagement',
        '60% faster resume tailoring',
        'Improved interview readiness scores'
      ],
      technologies: ['GPT-4o', 'OpenAI Whisper', 'RAG', 'Electron/desktop', 'Tailwind CSS', 'Docker'],
      duration: '6 weeks',
      teamSize: '2 Engineers',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual prep and scattered notes',
        after: 'AI‑guided prep with automatic notes',
        improvement: '70% engagement lift'
      }
    },
    {
      id: 'ai-erp-assistant',
      title: 'DataFlow Pro - Enterprise Data Intelligence Platform',
      industry: 'manufacturing',
      client: 'Upwork Client Project',
      challenge: 'Operational questions over ERP data required analysts and manual SQL/report lookups.',
      solution: 'Delivered a LangChain + FAISS assistant that answers natural‑language questions across structured (SQL/CSV) and unstructured (PDF) data sources with guardrails.',
      results: [
        '50% faster enterprise data retrieval',
        'Lower analyst workload',
        'Self‑serve insights for teams'
      ],
      technologies: ['LangChain', 'FAISS', 'LLMs', 'SQL', 'CSV', 'PDF'],
      duration: '2 months',
      teamSize: '2 Engineers',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual SQL and static reports',
        after: 'Natural language answers over ERP data',
        improvement: '50% faster retrieval'
      }
    },
    {
      id: 'pdf-rag-chatbot-milvus',
      title: 'DocuMind AI - Intelligent Document Assistant',
      industry: 'finance',
      client: 'Upwork Client Project',
      challenge: 'Critical knowledge was locked in PDFs, making search and QA slow and unreliable.',
      solution: 'Built a Streamlit chatbot backed by Milvus vector DB and OpenAI API for grounded answers with citations and source attribution.',
      results: [
        '40% improvement in retrieval accuracy',
        'Trustworthy answers with sources',
        'Faster document QA'
      ],
      technologies: ['Streamlit', 'Milvus', 'OpenAI API', 'RAG'],
      duration: '4 weeks',
      teamSize: '1 Engineer',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Manual search across PDFs',
        after: 'NL chat with citations',
        improvement: '40% accuracy lift'
      }
    },
    {
      id: 'movie-agent',
      title: 'CineMatch AI - Smart Entertainment Discovery',
      industry: 'retail',
      client: 'Upwork Client Project',
      challenge: 'Users struggled to discover relevant content across platforms with keyword‑only search.',
      solution: 'Shipped a LangChain agent using the TMDB API that interprets natural‑language intents and returns precise, ranked suggestions with filters.',
      results: [
        '90%+ success in understanding queries',
        'Higher user satisfaction',
        'Reduced search friction'
      ],
      technologies: ['LangChain', 'LLMs', 'TMDB API', 'Agents'],
      duration: '3 weeks',
      teamSize: '1 Engineer',
      image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      metrics: {
        before: 'Keyword‑only search',
        after: 'Conversational discovery',
        improvement: '90%+ query understanding'
      }
    }


  ], []);

  const industries = useMemo(() => [
    { id: 'all', name: 'All Industries', icon: 'bi-grid' },
    { id: 'healthcare', name: 'Healthcare', icon: 'bi-heart-pulse' },
    { id: 'finance', name: 'Finance', icon: 'bi-bank' },
    { id: 'retail', name: 'Retail', icon: 'bi-shop' },
    { id: 'transport', name: 'Transportation', icon: 'bi-truck' },
    { id: 'manufacturing', name: 'Manufacturing', icon: 'bi-gear' },
    { id: 'education', name: 'Education', icon: 'bi-mortarboard' },
    { id: 'cybersecurity', name: 'Cybersecurity', icon: 'bi-shield-check' }
  ], []);

  const countsByIndustry = useMemo(() => {
    const m = Object.fromEntries(industries.filter(i=>i.id!=='all').map(i => [i.id, 0]));
    caseStudies.forEach(cs => { if (m[cs.industry] !== undefined) m[cs.industry] += 1; });
    return m;
  }, [caseStudies, industries]);

  const filteredCaseStudies = selectedIndustries.length === 0 
    ? caseStudies 
    : caseStudies.filter(study => selectedIndustries.includes(study.industry));

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '3px',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        zIndex: 9999,
        transition: 'width 0.1s ease-out'
      }}></div>

      {/* Breadcrumb Navigation */}
      <div style={{
        position: 'fixed',
        top: '65px',
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(14, 165, 233, 0.1)',
        padding: '1rem 0',
        zIndex: 999,
        transition: 'all 0.3s ease'
      }}>
        <div className="container">
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Link to="/" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              <i className="bi bi-house"></i>
              <span>Home</span>
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <Link to="/insights" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '0.9rem',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              Insights
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.8rem' }}></i>
            <span style={{
              color: '#0ea5e9',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>
              Case Studies
            </span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="case-studies-hero-section" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        paddingTop: '130px'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>


        <div className="container" style={{ position: 'relative', zIndex: 3 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">

              
              {/* Industry Icon */}
              <div className="industry-icon-large mb-4" style={{
                display: 'inline-block',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '2px solid rgba(14, 165, 233, 0.3)',
                borderRadius: '50%',
                padding: '2rem',
                backdropFilter: 'blur(15px)',
                boxShadow: '0 8px 25px rgba(14, 165, 233, 0.2)'
              }}>
                <span style={{ fontSize: '4rem' }}>📊</span>
              </div>

              {/* Main Title */}
              <h1 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '800',
                color: '#ffffff',
                marginBottom: '2rem',
                lineHeight: '1.2',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
              }}>
                Case Studies & Success Stories
              </h1>
              
              {/* Subtitle */}
              <h2 style={{
                fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: '400',
                lineHeight: '1.4',
                marginBottom: '1rem'
              }}>
                Real-world AI implementations that drive measurable business impact
              </h2>

              {/* Description */}
              <p style={{
                fontSize: '1.1rem',
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '800px',
                margin: '0 auto 3rem',
                lineHeight: '1.6'
              }}>
                Explore detailed case studies showcasing how our AI solutions have transformed operations, reduced costs, and accelerated growth across diverse industries.
              </p>

              {/* CTA Buttons */}
              <div className="hero-cta" style={{
                display: 'flex',
                gap: '1.5rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '3rem'
              }}>
                <Link to="/contact" style={{
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '1.4rem 3.5rem',
                  borderRadius: '60px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)',
                  minWidth: '220px',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.5), 0 10px 30px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 10px 30px rgba(14, 165, 233, 0.4), 0 6px 20px rgba(0, 0, 0, 0.1)';
                }}>
                  <i className="bi bi-rocket-takeoff"></i>
                  Start Your Project
                </Link>

                <Link to="/testimonials" style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '2px solid rgba(14, 165, 233, 0.3)',
                  color: '#ffffff',
                  padding: '1.4rem 3.5rem',
                  borderRadius: '60px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(20px)',
                  minWidth: '220px',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-5px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.target.style.borderColor = 'rgba(14, 165, 233, 0.6)';
                  e.target.style.boxShadow = '0 15px 40px rgba(14, 165, 233, 0.2), 0 10px 30px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.target.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                  e.target.style.boxShadow = 'none';
                }}>
                  <i className="bi bi-chat-quote"></i>
                  Read Testimonials
                </Link>
              </div>
              
              {/* Stats Grid */}
              <div style={{ marginBottom: '3rem' }}>
                <div className="row g-4 justify-content-center">
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-rocket-takeoff"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>10+</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Projects Delivered</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-currency-dollar"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>$5M+</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Value Delivered</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-heart-fill"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>98%</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Client Satisfaction</div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '20px',
                      padding: '1.5rem',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <div style={{
                        fontSize: '2rem',
                        color: '#0ea5e9',
                        marginBottom: '0.5rem'
                      }}>
                        <i className="bi bi-building"></i>
                      </div>
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: '800',
                        color: '#ffffff',
                        marginBottom: '0.5rem'
                      }}>5+</div>
                      <div style={{
                        fontSize: '0.9rem',
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontWeight: '500'
                      }}>Industries Served</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
                marginBottom: '2rem'
              }}>
                <a href="#case-studies-grid" style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  color: '#1e293b',
                  padding: '1.2rem 3rem',
                  borderRadius: '50px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 8px 25px rgba(255, 255, 255, 0.2), 0 4px 15px rgba(0, 0, 0, 0.1)',
                  minWidth: '220px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(255, 255, 255, 0.3), 0 8px 25px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.2), 0 4px 15px rgba(0, 0, 0, 0.1)';
                }}>
                  <i className="bi bi-briefcase-fill"></i>
                  See Our Work
                </a>
                
                <Link to="/contact" style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: '1.2rem 3rem',
                  borderRadius: '50px',
                  fontWeight: '700',
                  fontSize: '1.1rem',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(15px)',
                  minWidth: '220px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}>
                  <i className="bi bi-calendar3"></i>
                  Schedule a Demo
                </Link>
              </div>
              
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
        `}</style>
      </section>

      {/* Industry Filter */}
      <section className="industry-filter-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="filter-header">
                <h3>Filter by Industry</h3>
                <p>Select industries to view relevant case studies</p>
              </div>
              <div className="industry-filter" role="toolbar" aria-label="Filter case studies by industry">
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${selectedIndustries.length === 0 ? 'active' : ''} ${isFiltering ? 'loading' : ''}`}
                    onClick={() => toggleIndustry('all')}
                    aria-pressed={selectedIndustries.length === 0}
                    disabled={isFiltering}
                  >
                    <i className="bi bi-grid"></i>
                    All Industries
                  </button>
                  {industries.filter(i=>i.id!=='all').map((industry) => {
                    const active = selectedIndustries.includes(industry.id);
                    const count = countsByIndustry[industry.id] ?? 0;
                    return (
                      <button
                        key={industry.id}
                        className={`filter-btn ${active ? 'active' : ''} ${isFiltering ? 'loading' : ''}`}
                        onClick={() => toggleIndustry(industry.id)}
                        aria-pressed={active}
                        disabled={isFiltering}
                      >
                        <i className={industry.icon}></i>
                        {industry.name} ({count})
                      </button>
                    );
                  })}
                </div>
                {selectedIndustries.length > 0 && (
                  <div className="filter-actions">
                    <button 
                      className="clear-filters-btn"
                      onClick={() => {
                        setIsFiltering(true);
                        setSelectedIndustries([]);
                        setTimeout(() => {
                          const caseStudiesSection = document.getElementById('case-studies-grid');
                          if (caseStudiesSection) {
                            caseStudiesSection.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'start' 
                            });
                          }
                          setIsFiltering(false);
                        }, 300);
                      }} 
                      aria-label="Clear all filters"
                      disabled={isFiltering}
                    >
                      <i className="bi bi-x-circle"></i> Clear All Filters
                    </button>
                    <span className="active-filters-count">
                      {selectedIndustries.length} filter{selectedIndustries.length !== 1 ? 's' : ''} active
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section id="case-studies-grid" className="py-5">
        <div className="container">
          <div className="row g-4">
            {filteredCaseStudies.map((study) => (
              <div key={study.id} className="col-12 col-lg-6">
                <div className="case-study-card-enterprise">
                  {/* Header Section */}
                  <div className="case-study-header">
                    <div className="case-study-image-container">
                      <img src={study.image} alt={study.title} className="case-study-image" loading="lazy" />
                      <div className="case-study-overlay">
                        <div className={`industry-badge-enterprise ${industryGradient[study.industry] || ''}`}>
                          <i className={`bi bi-${industries.find(i => i.id === study.industry)?.icon.split('-')[1]}`}></i>
                          {industries.find(i => i.id === study.industry)?.name}
                        </div>
                        <div className="verification-badge">
                          <i className="bi bi-patch-check-fill"></i>
                          Verified Project
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="case-study-content-enterprise">
                    {/* Title */}
                    <div className="case-study-meta">
                      <h3 className="case-study-title-enterprise">{study.title}</h3>
                    </div>

                    {/* Performance Metrics */}
                    <div className="performance-metrics">
                      <div className="metrics-grid">
                        <div className="metric-card">
                          <div className="metric-header">Before</div>
                          <div className="metric-content">{study.metrics.before}</div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-header">After</div>
                          <div className="metric-content">{study.metrics.after}</div>
                        </div>
                        <div className="metric-card highlight">
                          <div className="metric-header">Improvement</div>
                          <div className="metric-content">{study.metrics.improvement}</div>
                        </div>
                      </div>
                    </div>

                    {/* Project Details */}
                    <div className="project-details">
                      <div className="detail-section">
                        <h4 className="section-title">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Business Challenge
                        </h4>
                        <p className="section-content">{study.challenge}</p>
                      </div>

                      <div className="detail-section">
                        <h4 className="section-title">
                          <i className="bi bi-gear-wide-connected me-2"></i>
                          Technical Solution
                        </h4>
                        <p className="section-content">{study.solution}</p>
                      </div>

                      <div className="detail-section">
                        <h4 className="section-title">
                          <i className="bi bi-graph-up-arrow me-2"></i>
                          Measurable Results
                        </h4>
                        <ul className="results-list">
                          {study.results.map((result, index) => (
                            <li key={index} className="result-item">
                              <i className="bi bi-check-circle-fill"></i>
                              {result}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Technical Stack */}
                    <div className="technical-stack">
                      <h4 className="section-title">
                        <i className="bi bi-code-slash me-2"></i>
                        Technology Stack
                      </h4>
                      <div className="tech-tags-enterprise">
                        {study.technologies.map((tech, index) => (
                          <span key={index} className="tech-tag-enterprise">{tech}</span>
                        ))}
                      </div>
                    </div>

                    {/* Project Specifications */}
                    <div className="project-specs">
                      <div className="spec-item">
                        <span className="spec-label">Duration:</span>
                        <span className="spec-value">{study.duration}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Team Size:</span>
                        <span className="spec-value">{study.teamSize}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="case-study-actions">
                      <Link to="/contact" style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        color: '#1e293b',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '25px',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)',
                        marginRight: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(255, 255, 255, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)';
                      }}>
                        <i className="bi bi-calendar-check"></i>
                        Schedule Consultation
                      </Link>
                      <Link to="/contact" style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        color: '#ffffff',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '25px',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(15px)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}>
                        <i className="bi bi-chat-dots"></i>
                        Discuss Similar Project
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>




      {/* Our Commitment Section */}
      <div id="commitment">
        <OurCommitment />
      </div>

      {/* CTA Section */}
      <div id="cta">
        <CTA {...generateCTAMessage('case-studies')} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </>
  );
};

export default CaseStudiesPage; 